/** Feature storage keys remain stable when a public website address changes. */
export function featurePath(kind: string): string {
  return kind === "shop" ? "/merch" : `/${kind}`;
}

/** Saved Shop documents keep their identity; their website links now lead to Merch. */
export function canonicalFeaturePath(path: string): string {
  const chapter = path.match(/^\/matchup\/([a-z]+)\/?(?=[?#]|$)/)?.[1];
  if (chapter && (bibleChapterKeys as readonly string[]).includes(chapter))
    return path.replace(/^\/matchup\/[a-z]+\/?/, `/bible/${chapter}`);
  return path.replace(/^\/shop(?=\/|[?#]|$)/, "/merch");
}

export function featureKindForPath(path: string): string {
  return path === "/live"
    ? "matchups"
    : path === "/merch" || path === "/shop"
      ? "shop"
      : path.slice(1);
}

/** Only public page reads redirect. The caller must first resolve a creator host. */
export function legacyMerchRedirect(request: Request): string | null {
  if (request.method !== "GET" && request.method !== "HEAD") return null;
  const url = new URL(request.url);
  if (url.pathname !== "/shop" && !url.pathname.startsWith("/shop/")) return null;
  return `${canonicalFeaturePath(url.pathname)}${url.search}${url.hash}`;
}

/** Old imported public links keep working after a creator adopts the native site. */
export function legacyCreatorPageRedirect(request: Request): string | null {
  if (!["GET", "HEAD"].includes(request.method)) return null;
  const url = new URL(request.url);
  const auth =
    url.pathname === "/login" ? "/sign-in" : url.pathname === "/signup" ? "/sign-up" : null;
  if (auth) {
    if (!url.searchParams.has("returnTo") && url.searchParams.has("return_to")) {
      url.searchParams.set("returnTo", url.searchParams.get("return_to")!);
      url.searchParams.delete("return_to");
    }
    return `${auth}${url.search}${url.hash}`;
  }
  const chapter = canonicalFeaturePath(url.pathname);
  if (chapter.startsWith("/bible/") && chapter !== url.pathname)
    return `${chapter}${url.search}${url.hash}`;
  if (
    !/^\/matchup\/[a-z0-9-]{1,120}(?:\/(?:early-game|mid-game|late-game|setup))?\/?$/.test(
      url.pathname,
    )
  )
    return null;
  return `${url.pathname.replace(/^\/matchup\//, "/matchups/")}${url.search}${url.hash}`;
}

/**
 * Imported sites' other old addresses. These are names a creator may also give a page of their
 * own, so they move only when the site has nothing there: callers ask after a page wasn't found.
 */
export function legacyMissingPageRedirect(request: Request): string | null {
  if (!["GET", "HEAD"].includes(request.method)) return null;
  const url = new URL(request.url);
  const path = withoutSlash(url.pathname);
  const target =
    path === "/seminars"
      ? "/library"
      : /^\/posts(?:\/[a-z0-9-]{1,120})?$/.test(path)
        ? path.replace(/^\/posts/, "/blog")
        : null;
  return target ? `${target}${url.search}${url.hash}` : null;
}

/**
 * The chapter an imported site's `/bible?chapter=<key>` names (Porio's "comps" is Synergies), or
 * null. Callers open it only where that chapter is published.
 */
export function legacyBibleChapter(request: Request): string | null {
  if (!["GET", "HEAD"].includes(request.method)) return null;
  const url = new URL(request.url);
  const asked = url.searchParams.get("chapter");
  if (withoutSlash(url.pathname) !== "/bible" || asked === null) return null;
  const key = asked === "comps" ? "synergies" : asked;
  return (bibleChapterKeys as readonly string[]).includes(key) ? key : null;
}

const withoutSlash = (path: string) => (path.length > 1 ? path.replace(/\/$/, "") : path);
import { bibleChapterKeys } from "./bible-chapter-keys";
